import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, Button, Col, Row, Input, Select, Tag, Alert, message } from 'antd';
import { withRouter } from 'react-router-dom';
import { Segment } from '@fxtr/i18n';
import config from '$config';
import { isRenault } from '$app/brand';

import { getAllGarages } from '$app/common/api';
import {
  SEGMENT_SELECTION_CHANGED_EVENT,
} from '$app/segmentSelection';

import { Header, Title } from '$app/shared/components/Header';
import { Container } from '$app/shared/components/Container';
import Loading from '$app/shared/components/Loading';
import { useMaiaT } from '$app/i18n/useMaiaT';
import { isGarageCsvImportEnabled } from '../../utils/isGarageCsvImportEnabled';

import debounce from 'lodash/debounce';
import toLower from 'lodash/toLower';
import './style.css';

const isMotrio = config.segmentGroup === 'MOTRIO';
const canImportGarages = isGarageCsvImportEnabled(config);
const showDeliveryAndRates = !isMotrio && !isRenault;
const showPostcode = !isRenault;
const showActions = !isMotrio && !isRenault;

const DEFAULT_SEGMENT_FLAG = '🇬🇧';
// Built from entries so undefined segments (absent from the resolved
// @fxtr/i18n version) don't create a stray "undefined" key that would
// shadow the DEFAULT_SEGMENT_FLAG fallback for garages with no segment.
const SEGMENT_FLAGS = Object.fromEntries(
  [
    [Segment.Fixter_UK, '🇬🇧'],
    [Segment.Fixter_FR, '🇫🇷'],
    [Segment.Motrio_UK, '🇬🇧'],
    [Segment.Motrio_FR, '🇫🇷'],
    [Segment.Motrio_IT, '🇮🇹'],
    [Segment.Motrio_ES, '🇪🇸'],
    [Segment.Motrio_PT, '🇵🇹'],
    [Segment.Motrio_NL, '🇳🇱'],
    [Segment.Motrio_RO, '🇷🇴'],
    [Segment.Motrio_PL, '🇵🇱'],
    [Segment.Renault_MA, '🇲🇦'],
  ].filter(([segment]) => segment != null)
);
const getSegmentFlag = (segment) => SEGMENT_FLAGS[segment] || DEFAULT_SEGMENT_FLAG;

const GARAGE_LIST_SEARCH_FIELDS = [
  'businessName',
  ...(isMotrio ? ['tresorId'] : []),
  'legalName',
  'status',
  'email',
  'phoneNumber',
  ...(showDeliveryAndRates ? ['deliveryOptions'] : []),
  ...(showPostcode ? ['postcode'] : []),
];

function GarageListPage(props) {
  const t = useMaiaT();
  const [dataSource, setDataSource] = useState([]);
  const [dataSourceOrigin, setDataSourceOrigin] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Keep the latest `t` in a ref so the fetch effect can show a localized error
  // without depending on `t`, which changes on every language switch and would
  // otherwise trigger a redundant re-fetch of language-independent garage data.
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let isMounted = true;
    // Monotonic id identifying the most recently started request. Because
    // `getAllGarages` is paginated and awaits multiple HTTP calls, concurrent
    // fetches (initial mount + segment switches) can resolve out of order.
    // Only the latest request is allowed to commit, so a slow earlier fetch
    // can't overwrite the table with garages for a previously selected segment.
    let latestRequestId = 0;

    async function fetchGarages() {
      const requestId = ++latestRequestId;
      const isStale = () => !isMounted || requestId !== latestRequestId;

      try {
        setIsLoading(true);
        setHasError(false);
        const garages = await getAllGarages(true, false);

        const ds = garages.map((garage) => {
          garage.deliveryOptions = (garage.deliveryOptions || []).map((option) => option.deliveryOption).join(',');

          return garage;
        });

        if (!isStale()) {
          setDataSource(ds);
          setDataSourceOrigin(ds);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('GarageListPage error: ', err);
        if (!isStale()) {
          setIsLoading(false);
          setHasError(true);
          message.error(tRef.current('maia.garageList.loadError', 'Failed to load garages. Please try again.'));
        }
      }
    }

    const handleSegmentChange = () => fetchGarages();

    fetchGarages();
    window.addEventListener(SEGMENT_SELECTION_CHANGED_EVENT, handleSegmentChange);

    return () => {
      isMounted = false;
      window.removeEventListener(SEGMENT_SELECTION_CHANGED_EVENT, handleSegmentChange);
    };
  }, []);

  function handleFilterChange(selection) {
    const data =
      selection === 'none'
        ? dataSourceOrigin
        : dataSourceOrigin.filter((dso) => selection.toLowerCase() === dso.status.toLowerCase());

    setDataSource(data);
  }

  const activeSearch = useMemo(
    () =>
      debounce((value) => {
        if (value.length === 0) {
          setDataSource(dataSourceOrigin);
        } else {
          const filtered = dataSourceOrigin.filter((source) =>
            GARAGE_LIST_SEARCH_FIELDS.reduce(
              (acc, fieldName) =>
                acc ||
                (source[fieldName] && source[fieldName].toLowerCase
                  ? source[fieldName].toLowerCase().indexOf(value) >= 0
                  : false),
              false
            )
          );

          setDataSource(filtered);
        }
      }, 200),
    [dataSourceOrigin]
  );

  function searchInputChange(e) {
    e.persist();
    const value = e.target.value.toLowerCase();
    activeSearch(value);
  }

  const columns = useMemo(
    () => [
      {
        title: t('maia.garageList.column.businessName', 'Business name'),
        dataIndex: 'businessName',
        key: 'businessName',
        width: '20%',
        render: (value, { segment }) => `${getSegmentFlag(segment)} ${value}`,
      },
      {
        title: t('maia.garageList.column.legalName', 'Legal name'),
        dataIndex: 'legalName',
        key: 'legalName',
        width: '15%',
      },
      {
        title: t('maia.garageList.column.status', 'Status'),
        dataIndex: 'status',
        key: 'status',
        width: '10%',
        render: (val) => {
          const color = {
            active: 'green',
            suspended: 'gold',
            deleted: 'red',
          };

          const style = { textAlign: 'center', width: '100%' };
          const normalized = toLower(val);
          const label =
            normalized === 'active'
              ? t('maia.garageInfo.status.active', 'Active')
              : normalized === 'suspended'
                ? t('maia.garageInfo.status.suspended', 'Suspended')
                : normalized === 'deleted'
                  ? t('maia.garageInfo.status.deleted', 'Deleted')
                  : val;

          return (
            <Tag color={color[normalized]} style={style}>
              {label}
            </Tag>
          );
        },
      },
      {
        title: t('maia.garageList.column.email', 'Email'),
        dataIndex: 'email',
        key: 'email',
        width: '15%',
      },
      {
        title: t('maia.garageList.column.phoneNumber', 'Phone number'),
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: '15%',
      },
      ...(showDeliveryAndRates
        ? [
            {
              title: t('maia.garageList.column.deliveryOptions', 'Delivery options'),
              dataIndex: 'deliveryOptions',
              key: 'deliveryOptions',
              width: '10%',
            },
            {
              title: t('maia.garageList.column.rates', 'Rates'),
              dataIndex: 'operations',
              key: 'operations',
              width: '10%',
              render: (text, record) => {
                const operations = record.operations || [];

                const motRate = operations.find((op) => op.garageOPId === 'MOT');
                const labourRate = operations.find((op) => op.garageOPId === 'Labour rate');

                return (
                  <div className="garage-rates">
                    {motRate && (
                      <div className="garage-rate">
                        {t('maia.garageList.rates.mot', 'MOT:')} £{motRate.garageOPPrice / 100}
                      </div>
                    )}
                    {labourRate && (
                      <div className="garage-rate">
                        {t('maia.garageList.rates.labour', 'Labour:')} £{labourRate.garageOPPrice / 100}
                      </div>
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
      ...(showPostcode
        ? [
            {
              title: t('maia.garageList.column.postcode', 'Post code'),
              dataIndex: 'postcode',
              key: 'postcode',
              width: '10%',
            },
          ]
        : []),
      ...(isMotrio
        ? [
            {
              title: t('maia.garageList.column.tresorId', 'Tresor ID'),
              dataIndex: 'tresorId',
              key: 'tresorId',
              width: '10%',
            },
          ]
        : []),
      ...(showActions
        ? [
            {
              title: t('maia.garageList.column.action', 'Action'),
              key: 'action',
              width: '10%',
              render: (text, record) => (
                <Button
                  type="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.history.push(`${record.garageId}/operations`);
                  }}
                >
                  {t('maia.garage.menu.operations', 'Operations')}
                </Button>
              ),
            },
          ]
        : []),
    ],
    [t, props.history]
  );

  const filterOptions = useMemo(
    () => [
      {
        label: t('maia.garageList.filter.groupStatus', 'Garage status'),
        options: [
          { value: 'Active', label: t('maia.garageInfo.status.active', 'Active') },
          ...(!isRenault
            ? [{ value: 'Suspended', label: t('maia.garageInfo.status.suspended', 'Suspended') }]
            : []),
          { value: 'Deleted', label: t('maia.garageInfo.status.deleted', 'Deleted') },
        ],
      },
    ],
    [t]
  );

  useEffect(
    () => () => {
      activeSearch.cancel();
    },
    [activeSearch]
  );

  if (isLoading) return <Loading />;

  return (
    <Container>
      <Header>
        <Row justify="space-around" align="middle">
          <Col span={6}>
            <Title>{t('maia.nav.garages', 'Garages')}</Title>
            <Button
              type="link"
              className="create-btn"
              style={{ padding: 0, lineHeight: '10px' }}
              onClick={() => props.history.push('new')}
            >
              <i className="fa fa-plus-circle fa-lg" aria-hidden="true" />{' '}
              {t('maia.garageList.createGarage', 'Create garage')}
            </Button>
            {canImportGarages && (
              <div>
                <Button type="link" style={{ padding: 0, marginRight: 12 }} onClick={() => props.history.push('/imports/new/create')}>
                  {t('maia.garageList.importCreate', 'Create garages from CSV')}
                </Button>
                <Button type="link" style={{ padding: 0, marginRight: 12 }} onClick={() => props.history.push('/imports/new/update')}>
                  {t('maia.garageList.importUpdate', 'Update garages from CSV')}
                </Button>
                <Button type="link" style={{ padding: 0 }} onClick={() => props.history.push('/imports')}>
                  {t('maia.garageList.importHistory', 'Imports')}
                </Button>
              </div>
            )}
          </Col>
          <Col span={18} style={{ textAlign: 'right' }}>
            <Select defaultValue="" onChange={handleFilterChange} style={{ width: 200, marginRight: 5 }}>
              <Select.Option value="" disabled>
                {t('maia.garageList.filter.placeholder', 'Select filter')}
              </Select.Option>
              <Select.Option value="none">{t('maia.garageList.filter.all', 'All')}</Select.Option>
              {filterOptions.map(({ label, options }) => (
                <Select.OptGroup label={label} key={label}>
                  {options.map(({ value, label: optionLabel }) => (
                    <Select.Option key={value} value={value}>
                      {optionLabel}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
            <Input.Search
              placeholder={t('maia.garageList.searchPlaceholder', 'Search')}
              onChange={searchInputChange}
              style={{ width: 200 }}
              defaultValue=""
            />
          </Col>
        </Row>
      </Header>
      {hasError && (
        <Alert
          type="error"
          showIcon
          style={{ margin: '15px' }}
          message={t('maia.garageList.loadError', 'Failed to load garages. Please try again.')}
        />
      )}
      {/* TODO: pagination */}
      <Table
        onRow={(record) => ({
          onClick: () => {
            props.history.push(record.garageId);
          },
        })}
        size="small"
        rowKey={(record) => record.garageId}
        pagination={{
          position: 'bottom',
          pageSize: 13,
        }}
        dataSource={dataSource}
        columns={columns.map((column) => {
          if (column.dataIndex === 'operations') {
            return column;
          }
          return {
            ...column,
            sorter: (a, b) => {
              if (!a[column.dataIndex]) return -1;
              if (!b[column.dataIndex]) return 1;
              return a[column.dataIndex]?.localeCompare(b[column.dataIndex]);
            },
          };
        })}
      />
    </Container>
  );
}

export default withRouter(GarageListPage);
